'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { ArrowRight, ArrowLeft, X } from 'lucide-react';
import { ProjectData, projectsData } from '@/lib/projects/data';

interface ProjectPageProps {
  project: ProjectData;
  prevProject: ProjectData | null;
  nextProject: ProjectData | null;
}

const iconMap: Record<string, string> = {
  Calendar: '📅', Image: '🖼️', Building: '🏢', CreditCard: '💳',
  Smartphone: '📱', Search: '🔍', Package: '📦', Star: '⭐',
  ShoppingCart: '🛒', Tag: '🏷️', BarChart2: '📊', PieChart: '📈',
  Bell: '🔔', Shield: '🛡️', Download: '⬇️', Map: '🗺️',
  Calculator: '🧮', Heart: '❤️', Phone: '📞', Video: '🎥',
  FileText: '📄', MessageSquare: '💬', Users: '👥', TrendingUp: '📈',
  Layers: '📚', Zap: '⚡', Target: '🎯', Wrench: '🔧',
  Plug: '🔌', RefreshCw: '🔄', Globe: '🌐', Eye: '👁️',
};

export default function ProjectPageTemplate({ project, prevProject, nextProject }: ProjectPageProps) {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [lightboxAlt, setLightboxAlt] = useState<string>('');

  const whatsappMessage = encodeURIComponent(`Hi, I have a project similar to ${project.title}. Can we discuss?`);
  const whatsappUrl = `https://wa.me/919001612303?text=${whatsappMessage}`;

  const relatedProjects = project.relatedProjects
    .map((slug) => projectsData.find((p) => p.slug === slug))
    .filter(Boolean) as ProjectData[];

  return (
    <main className="min-h-screen bg-background">
      {/* Lightbox */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-50 bg-background/95 flex items-center justify-center p-4"
          onClick={() => setLightboxSrc(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
        >
          <button
            className="absolute top-4 right-4 p-2 glass-card rounded-xl border border-border/60 text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setLightboxSrc(null)}
            aria-label="Close image viewer"
          >
            <X size={20} />
          </button>
          <div className="relative max-w-5xl w-full max-h-[85vh] rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <Image
              src={lightboxSrc}
              alt={lightboxAlt}
              width={1200}
              height={800}
              className="object-contain w-full h-full"
              style={{ maxHeight: '85vh' }}
            />
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="relative pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0 aurora-bg opacity-50" />
        <div className="relative max-w-screen-xl mx-auto px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-muted-foreground mb-8">
            <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
            <span>/</span>
            <Link href="/projects" className="hover:text-foreground transition-colors">Projects</Link>
            <span>/</span>
            <span className="text-foreground">{project.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="inline-block text-xs font-mono-code px-3 py-1.5 rounded-full bg-primary/15 text-primary border border-primary/30 mb-4">
                {project.category}
              </span>
              <h1 className="font-display font-bold text-hero-xl text-foreground mb-4 leading-tight">
                {project.title}
              </h1>
              <p className="text-base text-muted-foreground leading-relaxed mb-8">
                {project.description}
              </p>
              {/* Project metadata */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Project Type', value: project.projectType },
                  { label: 'Industry', value: project.industry },
                  { label: 'Status', value: project.status },
                  { label: 'Technology', value: project.technologies.slice(0, 2).join(' / ') },
                ].map((meta) => (
                  <div key={meta.label} className="glass-card rounded-xl p-3.5 border border-border/40">
                    <span className="text-[10px] font-mono-code text-muted-foreground/60 uppercase tracking-widest block mb-1">{meta.label}</span>
                    <span className="text-sm font-display font-semibold text-foreground">{meta.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-2xl">
              <Image
                src={project.heroImage}
                alt={project.heroImageAlt}
                fill
                className="object-cover"
                priority
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/30 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="section-label">Overview</span>
            <h2 className="font-display font-bold text-section-title text-foreground mt-3 mb-5">Project Overview</h2>
            <p className="text-muted-foreground leading-relaxed text-base">
              {project.description} This project represents Webmint's approach to {project.category.toLowerCase()} — combining thoughtful design with technical precision to deliver a product that meets real business objectives.
            </p>
          </div>
        </div>
      </section>

      {/* Challenge & Solution */}
      <section className="py-16 bg-muted/10">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="glass-card rounded-2xl p-7 border border-rose/20 bg-rose/5">
              <span className="text-xs font-mono-code text-rose/70 uppercase tracking-widest block mb-3">The Challenge</span>
              <h2 className="font-display font-bold text-xl text-foreground mb-4">What We Were Solving</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">{project.challenge}</p>
            </div>
            <div className="glass-card rounded-2xl p-7 border border-emerald/20 bg-emerald/5">
              <span className="text-xs font-mono-code text-emerald/70 uppercase tracking-widest block mb-3">The Solution</span>
              <h2 className="font-display font-bold text-xl text-foreground mb-4">How We Approached It</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">{project.solution}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="section-label">Functionality</span>
            <h2 className="font-display font-bold text-section-title text-foreground mt-3 mb-4">Key Features</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {project.features.map((feature, i) => (
              <div key={i} className="glass-card rounded-xl p-5 border border-border/40 hover:border-primary/30 transition-all duration-300">
                <div className="flex items-start gap-3">
                  <span className="text-xl flex-shrink-0">{iconMap[feature.icon] || '✦'}</span>
                  <div>
                    <h3 className="font-display font-bold text-sm text-foreground mb-1.5">{feature.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      {project.gallery.length > 0 && (
        <section className="py-16 bg-muted/10">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="section-label">Screenshots</span>
              <h2 className="font-display font-bold text-section-title text-foreground mt-3 mb-4">Project Gallery</h2>
              <p className="text-muted-foreground text-sm">Click any image to view full size</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {project.gallery.map((img, i) => (
                <button
                  key={i}
                  className="relative aspect-video rounded-2xl overflow-hidden group cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                  onClick={() => { setLightboxSrc(img.src); setLightboxAlt(img.alt); }}
                  aria-label={`View full size: ${img.alt}`}
                >
                  <Image
                    src={img.src}
                    alt={img.alt}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-500"
                    sizes="(max-width: 768px) 100vw, 50vw"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-background/0 group-hover:bg-background/20 transition-colors duration-300" />
                  {img.caption && (
                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <p className="text-xs text-foreground/80">{img.caption}</p>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Tech Stack */}
      <section className="py-16">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="section-label">Technology</span>
              <h2 className="font-display font-bold text-section-title text-foreground mt-3 mb-5">Technology Stack</h2>
              <p className="text-muted-foreground leading-relaxed mb-6 text-sm">
                The technologies used in this project were selected based on the specific requirements — performance, scalability, and maintainability.
              </p>
              <div className="flex flex-wrap gap-3">
                {project.technologies.map((tech) => (
                  <span key={tech} className="px-4 py-2 glass-card rounded-xl border border-border/40 text-sm font-display font-semibold text-foreground">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <span className="section-label">Design</span>
              <h2 className="font-display font-bold text-section-title text-foreground mt-3 mb-5">Design & UX</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">{project.designNotes}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Performance */}
      <section className="py-16 bg-muted/10">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="section-label">Performance</span>
            <h2 className="font-display font-bold text-section-title text-foreground mt-3 mb-5">Performance Approach</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{project.performanceApproach}</p>
          </div>
        </div>
      </section>

      {/* Results */}
      {project.results.length > 0 && (
        <section className="py-16">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="section-label">Deliverables</span>
              <h2 className="font-display font-bold text-section-title text-foreground mt-3 mb-4">What We Delivered</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-3xl mx-auto">
              {project.results.map((result, i) => (
                <div key={i} className="glass-card rounded-2xl p-6 border border-border/40 text-center">
                  <span className="font-display font-bold text-2xl text-primary block mb-1">{result.value}</span>
                  <span className="text-xs font-mono-code text-muted-foreground/60 uppercase tracking-widest block mb-2">{result.label}</span>
                  {result.description && <p className="text-xs text-muted-foreground">{result.description}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <section className="py-16 bg-muted/10">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
            <div className="flex items-center justify-between mb-10">
              <div>
                <span className="section-label">More Work</span>
                <h2 className="font-display font-bold text-section-title text-foreground mt-2">Related Projects</h2>
              </div>
              <Link href="/projects" className="btn-outline text-sm hidden sm:flex">
                All Projects <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedProjects.slice(0, 2).map((rp) => (
                <Link
                  key={rp.slug}
                  href={`/projects/${rp.slug}`}
                  className="group glass-card rounded-2xl overflow-hidden border border-border/40 hover:border-primary/30 card-hover block"
                >
                  <div className="relative h-44 overflow-hidden">
                    <Image
                      src={rp.heroImage}
                      alt={rp.heroImageAlt}
                      fill
                      className="object-cover group-hover:scale-105 transition-transform duration-500"
                      sizes="(max-width: 768px) 100vw, 50vw"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
                    <span className="absolute top-3 left-3 text-xs font-mono-code px-2.5 py-1 rounded-full bg-background/70 border border-border/60 text-muted-foreground">
                      {rp.category}
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display font-bold text-base text-foreground mb-2 group-hover:text-accent transition-colors">{rp.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">{rp.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 aurora-bg opacity-50" />
        <div className="relative max-w-screen-xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display font-bold text-section-title text-foreground mb-4">
            Have a Similar Project?
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-8 text-base">
            Let's build a digital experience tailored to your business.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link href="/#contact" className="btn-primary">
              Start Your Project <ArrowRight size={14} />
            </Link>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="16" height="16" fill="currentColor" className="text-emerald">
                <path d="M16 0C7.163 0 0 7.163 0 16c0 2.822.736 5.469 2.027 7.773L0 32l8.437-2.007A15.93 15.93 0 0016 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm7.27 20.122c-.398-.199-2.355-1.162-2.72-1.295-.365-.133-.63-.199-.896.199-.265.398-1.029 1.295-1.261 1.561-.232.265-.465.298-.863.1-.398-.199-1.681-.619-3.203-1.977-1.184-1.057-1.983-2.362-2.215-2.76-.232-.398-.025-.613.174-.811.179-.178.398-.465.597-.697.199-.232.265-.398.398-.664.133-.265.066-.497-.033-.697-.1-.199-.896-2.16-1.228-2.958-.323-.776-.651-.671-.896-.683l-.763-.013c-.265 0-.697.1-1.062.497-.365.398-1.394 1.362-1.394 3.322s1.427 3.854 1.626 4.12c.199.265 2.808 4.287 6.803 6.014.951.41 1.693.655 2.271.839.954.304 1.823.261 2.51.158.765-.114 2.355-.963 2.688-1.893.332-.93.332-1.727.232-1.893-.099-.166-.365-.265-.763-.464z"/>
              </svg>
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <div className="py-8 border-t border-border/30">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <Link href="/projects" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft size={14} />
              Back to Projects
            </Link>
            <div className="flex items-center gap-4">
              {prevProject && (
                <Link href={`/projects/${prevProject.slug}`} className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                  <ArrowLeft size={12} /> {prevProject.title}
                </Link>
              )}
              {prevProject && nextProject && <span className="text-border">|</span>}
              {nextProject && (
                <Link href={`/projects/${nextProject.slug}`} className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                  {nextProject.title} <ArrowRight size={12} />
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
